import i18next from 'i18next';

import {
  AVAILABLE_SHOP_WEBHOOK_FIELDS,
  LEGACY_SHOP_WEBHOOK_FIELDS,
} from 'constants/shops.constants';
import {
  CustomerDataCollectionOrder,
  OrderType,
  PayinOrderTimeoutConfigKey,
  ShopStatus,
  ShopWebhookField,
} from 'enums';
import type { Shop, ShopWebhookFieldConfig } from 'types/shop.type';

const getShopStatusLabel = (status: ShopStatus) => {
  const { t } = i18next;
  const key = `features.shops.shop_status.${status}`;
  return i18next.exists(key) ? t(key as any) : key;
};

const getPayinOrderTimeoutLabel = (status: PayinOrderTimeoutConfigKey) => {
  const { t } = i18next;
  const key = `features.shops.payin_timeout_config.${status}`;
  return t(key, { defaultValue: status });
};

const getCustomerDataCollectionOrder = (order: CustomerDataCollectionOrder) => {
  const { t } = i18next;
  const key = `features.shops.customer_data_collection_order.${order}`;
  return i18next.exists(key) ? t(key as any) : key;
};

const getShopStatusSelectTitle = () => {
  const { t } = i18next;
  const key = `components.shop_status_select.title`;
  return i18next.exists(key) ? t(key as any) : key;
};

const getWebhookFieldsName = (
  orderType: OrderType,
): 'payinWebhookFields' | 'payoutWebhookFields' =>
  orderType === OrderType.Payin ? 'payinWebhookFields' : 'payoutWebhookFields';

const getAvailableWebhookFields = (orderType: OrderType): ShopWebhookField[] =>
  AVAILABLE_SHOP_WEBHOOK_FIELDS[orderType];

const getWebhookFields = (
  shop: Pick<
    Shop,
    'payinWebhookFields' | 'payoutWebhookFields' | 'fullWebhook'
  >,
  orderType: OrderType,
): ShopWebhookField[] => {
  const fields = shop[getWebhookFieldsName(orderType)];
  if (fields != null) {
    return fields.map(({ field }) => field);
  }
  const availableFields = getAvailableWebhookFields(orderType);
  return shop.fullWebhook
    ? LEGACY_SHOP_WEBHOOK_FIELDS.filter((field) =>
        availableFields.includes(field),
      )
    : [];
};

const serializeWebhookFields = (
  fields: ShopWebhookField[],
): ShopWebhookFieldConfig[] => fields.map((field) => ({ field }));

export const shopUtils = {
  getWebhookFields,
  getWebhookFieldsName,
  getAvailableWebhookFields,
  serializeWebhookFields,
  getShopStatusLabel,
  getPayinOrderTimeoutLabel,
  getShopStatusSelectTitle,
  getCustomerDataCollectionOrder,
};
